import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IStoreAssetApiResponse } from 'models/store/i-store-asset-api-response.interface';
import { IWorldNftTradeResponse } from 'models/store/i-world-nft-trade-response.interface';

import { AssetDetailsField } from 'enums/params-fields';

import { addErrorMessageAction } from 'actions/global-actions';

import { fetchLandingAssets } from 'services/landing.service';
import { findExpectedAnnualYieldByTokenId } from 'services/nft-yields.service';
import { fetchWorldNftTrade } from 'services/world-nft-trade.service';

import { INftCardAsset } from 'components/nftCard/_new/types';

import {
  convertToNftCardAsset,
  isWorldNftTradeError,
  isWorldNftTradeSoldOut,
  isWorldNftTradeSuccess,
} from 'utils/world-nft';

import { queryKeys } from './query-keys';

// Fetch world NFT trade data
const fetchWorldNftTradeData = async (): Promise<IWorldNftTradeResponse | null> => {
  const response = await fetchWorldNftTrade();
  return response.data;
};

// Fetch asset details for world NFT
const fetchWorldNftAssets = async (tokenIds: string[]): Promise<IStoreAssetApiResponse[]> => {
  if (!tokenIds.length) return [];

  const response = await fetchLandingAssets(tokenIds, [AssetDetailsField.Contract]);
  return response.data || [];
};

// Fetch expected annual yield
const fetchWorldNftYield = async (tokenId: string): Promise<number | null> => {
  try {
    return await findExpectedAnnualYieldByTokenId(tokenId);
  } catch (error) {
    console.warn('Failed to fetch yield data, using default value');
    return null;
  }
};

export interface UseWorldNftQueryReturn {
  asset: INftCardAsset | null;
  rawTradeData: IWorldNftTradeResponse | null;
  assetDetails: IStoreAssetApiResponse | null;
  expectedAnnualYield: number | null;
  loading: boolean;
  error: string | null;
  isSoldOut: boolean;
}

export const useWorldNftQuery = (): UseWorldNftQueryReturn => {
  const dispatch = useDispatch();

  // Query for world NFT trade data
  const {
    data: tradeData,
    isLoading: tradeLoading,
    error: tradeError,
  } = useQuery({
    queryKey: queryKeys.worldNft.trade(),
    queryFn: fetchWorldNftTradeData,
  });

  // Check different response states
  const isErrorResponse = tradeData ? isWorldNftTradeError(tradeData) : false;
  const isSoldOutResponse = tradeData ? isWorldNftTradeSoldOut(tradeData) : false;
  const isSuccessResponse = tradeData ? isWorldNftTradeSuccess(tradeData) : false;

  // isSoldOut indicates any sold out state (error or sold out with data)
  const isSoldOut = isErrorResponse || isSoldOutResponse;

  // Get token ID from trade data (for success or sold out response with data)
  const hasAssetData = isSuccessResponse || isSoldOutResponse;
  const tokenId = hasAssetData && tradeData && tradeData.world_nft ? tradeData.world_nft.token_id : undefined;
  const tokenIdNumber = tokenId ? parseInt(tokenId, 10) : undefined;

  // Query for asset details
  const {
    data: assetDataArray = [],
    isLoading: assetLoading,
    error: assetError,
  } = useQuery({
    queryKey: queryKeys.worldNft.assets(tokenIdNumber ? [tokenIdNumber] : []),
    queryFn: () => fetchWorldNftAssets(tokenId ? [tokenId] : []),
    enabled: !!tokenId && hasAssetData, // Only run if we have valid token ID and asset data
  });

  // Query for expected annual yield
  const {
    data: expectedAnnualYield = null,
    isLoading: yieldLoading,
  } = useQuery({
    queryKey: queryKeys.worldNft.yield(tokenIdNumber || 0),
    queryFn: () => fetchWorldNftYield(tokenId!),
    enabled: !!tokenId && hasAssetData, // Only run if we have valid token ID and asset data
  });

  const assetDetails = assetDataArray[0] || null;

  // Handle errors
  useEffect(() => {
    if (tradeError) {
      const errorMessage =
        (tradeError as any)?.response?.data?.message || tradeError?.message || 'Failed to fetch world NFT trade data';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [tradeError, dispatch]);

  useEffect(() => {
    if (assetError) {
      const errorMessage =
        (assetError as any)?.response?.data?.message || assetError?.message || 'Failed to fetch world NFT asset data';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [assetError, dispatch]);

  // Handle sold out or error state from API
  useEffect(() => {
    if (tradeData && (isWorldNftTradeError(tradeData) || isWorldNftTradeSoldOut(tradeData))) {
      // Don't dispatch error for sold out state, just handle it gracefully
      console.warn('World NFT is sold out or unavailable:', tradeData.message);
    }
  }, [tradeData]);

  // Convert to NFT card asset format
  const asset =
    tradeData && assetDetails && hasAssetData
      ? convertToNftCardAsset(tradeData, assetDetails, expectedAnnualYield)
      : null;

  return {
    asset,
    rawTradeData: tradeData || null,
    assetDetails,
    expectedAnnualYield,
    loading: tradeLoading || assetLoading || yieldLoading,
    error: tradeError || assetError ? String(tradeError || assetError) : null,
    isSoldOut,
  };
};
